import { Grid, Skeleton, Stack, TextInput } from '@mantine/core';

import { FormSubsection } from '../../ui/FormSubsection.js';
import { LabelWithInfo } from '@/components/ui/LabelWithInfo.tsx';
import { INTEGER_INPUT_PROPS } from '../../form/numberFieldPresets.js';
import {
  ValidatedNumberInput,
  ValidatedSelect,
  ValidatedTextInput,
} from '../../form/validatedFields.js';
import type { AccidentForm } from './accidentFormFields.js';

interface GeneralInfoSectionProps {
  form: AccidentForm;
  disabled?: boolean;
  loading?: boolean;
  companyName?: string;
  plantName?: string;
}

export function GeneralInfoSection({
  form,
  disabled,
  loading,
  companyName,
  plantName,
}: GeneralInfoSectionProps) {
  if (loading) {
    return (
      <Grid>
        {Array.from({ length: 8 }, (_, i) => i).map((i) => (
          <Grid.Col span={{ base: 12, md: 6 }} key={i}>
            <Skeleton height={60} />
          </Grid.Col>
        ))}
      </Grid>
    );
  }

  return (
    <Stack>
      <FormSubsection title="Allgemeine Informationen" description="Wenn eine Umfirmierung stattgefunden hat, wenden Sie sich bitte an Ihren Ansprechpartner bei VDZ.">
        <Grid>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <TextInput label="Unternehmen" value={companyName ?? ''} disabled readOnly />
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 6 }}>
            <TextInput label="Werk" value={plantName ?? ''} disabled readOnly />
          </Grid.Col>
        </Grid>
      </FormSubsection>

      <FormSubsection title="Produktionsstatus">
        <Grid>
          <Grid.Col span={{ base: 12, md: 6 }}>
        <ValidatedSelect
          form={form}
          field="clinkerProductionDuringPeriod"
          fieldLabel="Klinkerherstellung im Zeitraum"
          label={
            <LabelWithInfo
              label="Klinkerherstellung im Zeitraum"
              info="Hat im Berichtszeitraum Klinkerherstellung stattgefunden?"
            />
          }
          placeholder="Bitte wählen"
          data={[
            { value: 'true', label: 'Ja' },
            { value: 'false', label: 'Nein' },
          ]}
          fromStored={(stored) => (stored == null ? null : String(stored))}
          toStored={(value) => (value === null ? null : value === 'true')}
          disabled={disabled}
        />
          </Grid.Col>
        </Grid>
      </FormSubsection>

      <FormSubsection title="Mitarbeiteranzahl" description="Anzahl der Mitarbeiter im Jahresdurchschnitt">
        <Grid>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <ValidatedNumberInput
              form={form}
              field="employeesTotal"
              kind="integer"
              label="Mitarbeiter gesamt"
              disabled={disabled}
              {...INTEGER_INPUT_PROPS}
            />
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 6 }}>
            <ValidatedNumberInput
              form={form}
              field="employeesAdmin"
              fieldLabel="Mitarbeiter in der Verwaltung"
              kind="integer"
              label="davon in der Verwaltung"
              disabled={disabled}
              {...INTEGER_INPUT_PROPS}
            />
          </Grid.Col>
        </Grid>
      </FormSubsection>

      <FormSubsection title="Ansprechpartner für eventuelle Rückfragen">
        <Grid>
          <Grid.Col span={{ base: 12, md: 4 }}>
            <ValidatedTextInput form={form} field="contactName" label="Name" fieldLabel="Ansprechpartner – Name" disabled={disabled} />
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 4 }}>
            <ValidatedTextInput form={form} field="contactPhone" label="Telefon" fieldLabel="Ansprechpartner – Telefon" disabled={disabled} />
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 4 }}>
            <ValidatedTextInput
              form={form}
              field="contactEmail"
              label="E-Mail"
              fieldLabel="Ansprechpartner – E-Mail"
              type="email"
              disabled={disabled}
            />
          </Grid.Col>
        </Grid>
      </FormSubsection>
    </Stack>
  );
}
